import React from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { IRequestMaterialSinglePosition } from '@entities/requestMaterial';
import { useRequestMaterialStageContext } from '@entities/requestMaterial/model/context/useRequestMaterialStageContext';
import { DateHelpers, formatDecimalInput } from '@shared/helpers';
import { Checkbox, Editable, UiDateInput, UiText } from '@shared/ui';

import cls from './RequestMaterialPosition.module.css';

interface RequestMaterialPositionProps {
    classes?: string;
    position: IRequestMaterialSinglePosition;
}

export const RequestMaterialPosition = ({ classes, position }: RequestMaterialPositionProps) => {
    const {
        selectedGuids,
        toggleSelected,
        positionsForm,
        onChangePositionForm,
        newlyAddedGuid,
        invalidGuids,
    } = useRequestMaterialStageContext();

    const isSelected = selectedGuids.has(position.guid);
    const { t } = useTranslation();

    const storedCount = positionsForm[position.guid]?.count ?? '';
    const displayCount = storedCount ? storedCount.replace('.', ',') : '0';

    const onChangeName = (value: string) => {
        onChangePositionForm(position.guid, 'name', value);
    };
    const onChangeComment = (value: string) => {
        onChangePositionForm(position.guid, 'comment', value);
    };

    const onChangeCount = (value: string) => {
        onChangePositionForm(position.guid, 'count', value.replace(',', '.'));
    };

    const onChangeDate = (date: string) => {
        onChangePositionForm(position.guid, 'date', date);
    };

    const onChangeUnit = (value: string) => {
        onChangePositionForm(position.guid, 'unit', value);
    };

    return (
        <div
            key={position.guid}
            className={cn(cls.RequestMaterialPosition, { [cls.selected]: isSelected })}
        >
            <div className={cls.checkboxCell}>
                <Checkbox
                    isDisabled={position.canceled}
                    checkBoxClassName={cls.checkbox}
                    isActive={isSelected}
                    onClick={() => toggleSelected(position.guid)}
                />
            </div>

            <div className={cls.nameCell}>
                <div className={cls.name}>
                    <Editable
                        readOnly={!position?.isManual}
                        placeholder={t('RequestMaterialPosition.return.name.Editable.placeholder')}
                        multiline
                        rows={1}
                        onSubmit={onChangeName}
                        value={positionsForm[position.guid]?.name ?? position.name}
                        textareaClassName={cls.nameTextarea}
                        className={cn(cls.nameClamped)}
                        autoFocus={newlyAddedGuid === position.guid}
                        isError={invalidGuids.has(position.guid)}
                    >
                        <UiText
                            align="left"
                            variant="mediumPlain"
                            title={positionsForm[position.guid]?.name ?? position.name}
                        >
                            {positionsForm[position.guid]?.name ?? position.name}
                        </UiText>
                    </Editable>
                    {position.canceled && (
                        <div className={cls.canceled}>
                            <UiText>
                                {t('RequestMaterialPosition.return.name.canceled.UiText')}
                            </UiText>
                        </div>
                    )}
                </div>

                <div className={cls.comment}>
                    <Editable
                        placeholder={t(
                            'RequestMaterialPosition.return.comment.Editable.placeholder'
                        )}
                        multiline
                        rows={2}
                        onSubmit={onChangeComment}
                        value={positionsForm[position.guid]?.comment ?? ''}
                        textareaClassName={cls.commentTextarea}
                        className={cn(cls.nameClamped)}
                        readOnly={position.canceled}
                        maxLength={1000}
                    >
                        <UiText title={positionsForm[position.guid]?.comment} variant="caption">
                            {positionsForm[position.guid]?.comment ?? ''}
                        </UiText>
                    </Editable>
                </div>
            </div>

            <div className={cls.dateText}>
                <UiText variant="mediumPlain">
                    {DateHelpers.dateFormatter({ date: position.deliveryDate })}
                </UiText>
            </div>

            <div className={cn(cls.date, !positionsForm[position.guid]?.date && cls.placeholder)}>
                <UiDateInput
                    disabled={position.canceled}
                    calendarClassName={cls.calendar}
                    placeholder={t(
                        'RequestMaterialPosition.return.position.UiDateInput.placeholder'
                    )}
                    value={positionsForm[position.guid]?.date ?? ''}
                    onChange={onChangeDate}
                    showIcon={false}
                    inputClassName={cls.dateInput}
                    minDate={DateHelpers.today().format('YYYY-MM-DD')}
                />
            </div>

            <div
                className={cn(
                    cls.count,
                    cls.truncatedCell,
                    !positionsForm[position.guid]?.count && cls.placeholder
                )}
            >
                <Editable
                    readOnly={position.canceled}
                    onSubmit={onChangeCount}
                    inputClassName={cls.countInput}
                    value={displayCount}
                    transform={formatDecimalInput}
                >
                    <UiText title={displayCount} classes={cls.truncated}>
                        {displayCount}
                    </UiText>
                </Editable>
            </div>

            <div
                className={cn(cls.remainText, {
                    [cls.remainNegative]:
                        position.remainAmount !== null && Number(position.remainAmount) <= 0,
                })}
            >
                <UiText>{position.remainAmount}</UiText>
            </div>
            <div
                className={cn(cls.remainText, cls.truncatedCell, {
                    [cls.remainNegative]:
                        position.remainAmount !== null && Number(position.remainAmount) <= 0,
                })}
            >
                <Editable
                    readOnly={!position?.isManual || position.canceled}
                    placeholder={t('RequestMaterialPosition.return.unit.Editable.placeholder')}
                    onSubmit={onChangeUnit}
                    inputClassName={cls.countInput}
                    value={positionsForm[position.guid]?.unit ?? position.unit}
                >
                    <UiText
                        title={positionsForm[position.guid]?.unit ?? position.unit}
                        classes={cls.truncated}
                    >
                        {positionsForm[position.guid]?.unit ?? position.unit}
                    </UiText>
                </Editable>
            </div>
        </div>
    );
};
